import React, { FC, useCallback } from "react";
import { useDialogShadow, IconTypesKeys } from "@evergis/ui";
import { getGeometryType } from "evergis/core/attributes/geometryTypes";
import { ResourceInfoDc } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { DEFAULT_ID_ATTRIBUTE_NAME } from "../../../../../evergis/constants";
import { useTablePreviewUrl } from "../../../../../hooks/preview/usePreviewUrl";
import { useTableInfoUpdate } from "../../../../../hooks/table/useTableInfoUpdate";
import { useTableWithRefs } from "../../../../../hooks/table/useTableWithRefs";
import { useTrigger } from "../../../../../hooks/useTrigger";
import { formatDate } from "../../../../../utils/date";
import { formatNumber } from "../../../../../utils/formatNumber";
import { Attributes } from "../../../DialogComponents/Attributes/Attributes";
import { DialogActions } from "../../../DialogComponents/DialogActions";
import { DialogTitle } from "../../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../../DialogComponents/FullScreenDialog";
import { ResourceMeta } from "../../../DialogComponents/ResourceMeta/ResourceMeta";
import { DialogContent, ProjectMetaBlock, RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { DataField, FieldValue, styledHeaderText } from "../../../styled";
import { useDialogInfo } from "../../../../../hooks/useDialogs";
import { getMetaFromResource, useResourceMeta } from "../../../../../hooks/useResourceMeta";
import { useAttributesErrors } from "../../hooks/useAttributesErrors";
import { createTableAttribute, useTableAttributes } from "../../hooks/useTableAttributes";
import { BlackIcon } from "./styled";
import { TableAttribute, TableAttributeItem } from "./TableAttributeItem";
import { isEditableType } from "../../../../../evergis/core/permissions";

// eslint-disable-next-line max-lines-per-function
export const TableSettings: FC = () => {
  const { t } = useTranslation();

  const {
    resources: [tableName],
    closeDialog,
  } = useDialogInfo(true);

  const { shadows, setScrollContainer } = useDialogShadow();
  const { table, layers, loading } = useTableWithRefs(tableName);
  const { columns, changedDate, objectsCount, geometryType, name } = table;
  const previewUrl = useTablePreviewUrl(name);
  const [meta, updateMeta] = useResourceMeta(getMetaFromResource(table as ResourceInfoDc, previewUrl));
  const [attributes, { setAttributes, updateAttribute, deleteAttribute, addAttribute }] = useTableAttributes(columns);
  const { update, tableUpdate, updating } = useTableInfoUpdate(table, layers, meta, attributes);
  const attributesErrors = useAttributesErrors(attributes);
  const isEditType = isEditableType(table.type);

  const onAddAttribute = useCallback(
    () => addAttribute(createTableAttribute(`${attributes.length}`)),
    [addAttribute, attributes],
  );

  useTrigger(tableUpdate && !updating, closeDialog);

  return (
    <FullScreenDialog
      dialogContext={{
        title: {
          text: styledHeaderText(t("editResource.tableProperties", { ns: "dialogs" })),
          icon: "settings",
        },
        meta: {
          aliasLabel: t("editResource.tableName", { ns: "dialogs" }),
          descriptionLabel: t("editResource.tableDescription", { ns: "dialogs" }),
          loaderLabel: t("editResource.uploadImage", { ns: "dialogs" }),
        },
        applyText: t("actions.save", { ns: "common" }),
        attributes: {
          label: t("editResource.data", { ns: "dialogs" }),
          description: t("editResource.attributesListDescription", { ns: "dialogs" }),
          icon: "data",
        },
      }}
    >
      <DialogTitle displayShadow={shadows.upper} />
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          <ProjectMetaBlock>
            <ResourceMeta meta={meta} updateMeta={updateMeta} loading={loading}>
              <DataField column>
                <FieldValue field={t("editResource.updated", { ns: "dialogs" })} value={formatDate(changedDate)} />
                <DataField>
                  <FieldValue
                    field={t("editResource.geometryType", { ns: "dialogs" })}
                    value={
                      <>
                        {geometryType && (
                          <BlackIcon kind={`geom_${geometryType}` as IconTypesKeys} style={{ marginRight: "0.5rem" }} />
                        )}
                        <span>{t(getGeometryType(geometryType), { ns: "common" })}</span>
                      </>
                    }
                  />
                  <FieldValue
                    field={t("editResource.totalFeatures", { ns: "dialogs" })}
                    value={formatNumber(objectsCount)}
                  />
                </DataField>
              </DataField>
            </ResourceMeta>
          </ProjectMetaBlock>
        </RestrictedWidthContainer>
        <Attributes
          isEditType={isEditType}
          attributes={attributes}
          setAttributes={setAttributes}
          onAddAttribute={onAddAttribute}
          loading={loading}
        >
          {(attribute: TableAttribute) => (
            <TableAttributeItem
              attribute={attribute}
              isEditType={isEditType}
              idField={DEFAULT_ID_ATTRIBUTE_NAME}
              error={attributesErrors.get(attribute.id)}
              updateAttribute={updateAttribute}
              deleteAttribute={deleteAttribute}
            />
          )}
        </Attributes>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyDisabled={!meta.alias || !!attributesErrors.size}
        onApply={update}
        isWaiting={updating}
      />
    </FullScreenDialog>
  );
};
